import { useEffect, useState } from 'react';
import { Public } from '../api.js';
import { FormField, Switch } from '../components/FormField.jsx';
import { PageHeader } from '../components/PageHeader.jsx';

const empty = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  position: '',
  experience: '',
  preferredTeamId: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  medicalNotes: '',
  waiverAccepted: false,
  marketingOptIn: true,
};

export default function Join() {
  const [form, setForm] = useState(empty);
  const [teams, setTeams] = useState([]);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Public.teams().then((r) => setTeams(r.teams)).catch(() => setTeams([]));
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await Public.joinRequest(form);
      setDone(true);
    } catch (e) {
      setErr(e.data?.issues ? e.data.issues.map((i) => i.message).join(', ') : e.message);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto mt-16 md:mt-24 px-4 text-center">
        <div className="eyebrow text-brand mb-3">Application received</div>
        <h1 className="font-display text-5xl mb-5">See you at training.</h1>
        <p className="text-ink-2">
          A coach will reach out to <strong className="text-ink">{form.email}</strong> within a few days.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <PageHeader eyebrow="New players" title="Join Blaze RFC." description="Tell us a bit about yourself. No experience necessary." />
      <form onSubmit={submit} className="card space-y-5">
        <Fieldset title="About you">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="First name" required>
              <input className="input" value={form.firstName} onChange={(e) => update('firstName', e.target.value)} required />
            </FormField>
            <FormField label="Last name" required>
              <input className="input" value={form.lastName} onChange={(e) => update('lastName', e.target.value)} required />
            </FormField>
          </div>
          <FormField label="Email" required>
            <input type="email" className="input" value={form.email} onChange={(e) => update('email', e.target.value)} required />
          </FormField>
          <FormField label="Phone">
            <input className="input" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
          </FormField>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Date of birth">
              <input type="date" className="input" value={form.dateOfBirth} onChange={(e) => update('dateOfBirth', e.target.value)} />
            </FormField>
            <FormField label="Preferred position" hint="e.g. flanker, fly-half, forward, back">
              <input className="input" value={form.position} onChange={(e) => update('position', e.target.value)} />
            </FormField>
          </div>
          {teams.length > 0 && (
            <FormField label="Preferred side">
              <select className="input" value={form.preferredTeamId} onChange={(e) => update('preferredTeamId', e.target.value)}>
                <option value="">No preference</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.tier ? ` — ${t.tier}` : ''}
                  </option>
                ))}
              </select>
            </FormField>
          )}
          <FormField label="Rugby experience" hint="Optional. Be honest — we coach all levels.">
            <textarea rows={3} className="input" value={form.experience} onChange={(e) => update('experience', e.target.value)} />
          </FormField>
        </Fieldset>

        <Fieldset title="Safety">
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Emergency contact name">
              <input className="input" value={form.emergencyContactName} onChange={(e) => update('emergencyContactName', e.target.value)} />
            </FormField>
            <FormField label="Emergency contact phone">
              <input className="input" value={form.emergencyContactPhone} onChange={(e) => update('emergencyContactPhone', e.target.value)} />
            </FormField>
          </div>
          <FormField label="Medical notes" hint="Allergies, past injuries. Shared only with coaches and medics.">
            <textarea rows={2} className="input" value={form.medicalNotes} onChange={(e) => update('medicalNotes', e.target.value)} />
          </FormField>
        </Fieldset>

        <Fieldset title="Agreements">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" className="check mt-1" checked={form.waiverAccepted} onChange={(e) => update('waiverAccepted', e.target.checked)} required />
            <span className="text-sm text-ink-2">
              I accept the club's <a className="text-brand underline underline-offset-4">liability waiver</a> and code of conduct.
            </span>
          </label>
          <Switch
            checked={form.marketingOptIn}
            onChange={(v) => update('marketingOptIn', v)}
            label="Send me club news"
            hint="Fixtures, socials, fundraisers"
          />
        </Fieldset>

        {err && <p className="text-sm text-danger">{err}</p>}
        <button type="submit" className="btn-primary w-full h-11" disabled={busy}>
          {busy ? 'Sending…' : 'Submit application'}
        </button>
      </form>
    </div>
  );
}

function Fieldset({ title, children }) {
  return (
    <fieldset className="space-y-4">
      <div className="eyebrow pb-2 border-b border-line">{title}</div>
      {children}
    </fieldset>
  );
}
